import type { Tiered } from "./bank";

/**
 * Themed packs. Each one adds extra words to the core bank (bank.ts) around
 * one subject, so a whole theme can be seeded, reviewed or switched off in
 * one place. The engine merges every pack in PACKS into the core lists.
 *
 * To add a pack: copy one below, give it a new id, fill in whichever slots
 * it needs (all are optional, and so is every tier), and add it to PACKS.
 * The first four came from the "Ideas for later" notes that used to live in
 * Settings. New ones go here directly (see docs/COPY.md).
 */
export interface Pack {
  id: string;
  label: string;
  curses?: Partial<Tiered<string>>;
  pnouns?: Partial<Tiered<string>>;
  nouns?: Partial<Tiered<string>>;
  endings?: Partial<Tiered<string>>;
  similes?: Partial<Tiered<string>>;
}

const office: Pack = {
  id: "office",
  label: "Office life",
  pnouns: { 0: ["admin"], 1: ["admin"] },
  endings: {
    0: ["to minute", "to put in a spreadsheet"],
    1: ["to reconcile", "to circle back on"],
    2: ["to reconcile by hand at 4:58 on a Friday"],
  },
  similes: {
    0: ["team meeting that could've been an email", "reply-all about the office fridge", "fire drill in the drizzle"],
    1: [
      "reconciliation that's out by 3p", "calendar invite called 'quick chat'", "spreadsheet full of merged cells",
      "hour-long stand-up",
    ],
    2: [
      "month-end close with a hangover", "all-hands with your camera on and your trousers off",
      "reply-all apocalypse with the CEO copied in",
    ],
  },
};

const longHaul: Pack = {
  id: "long-haul",
  label: "NZ to UK flights",
  endings: { 1: ["to sit through in economy"], 2: ["to sit through in the seat by the bogs"] },
  similes: {
    0: ["middle seat from Auckland to Heathrow", "4am layover in Dubai", "lost suitcase in Singapore"],
    1: ["27-hour flight in a middle seat", "screaming toddler over the Indian Ocean", "reclined seat in your lap for 11 hours"],
    2: ["long-haul in the seat that doesn't recline, next to the bogs", "food-poisoned layover in Doha"],
  },
};

const britFilms: Pack = {
  id: "brit-films",
  label: "90s and 2000s British films",
  similes: {
    0: ["rainy Richard Curtis wedding", "Full Monty rehearsal in the job centre"],
    1: ["Lock, Stock card game you're losing", "wet weekend in a Shane Meadows film"],
    2: ["dive into the worst toilet in Scotland", "Sexy Beast pep talk from Don Logan"],
  },
};

const superbikes: Pack = {
  id: "superbikes",
  label: "2000s British Superbikes and MotoGP",
  endings: { 1: ["to ride out"], 2: ["to ride out with a tank-slapper"] },
  similes: {
    0: ["wet British Superbikes round at Oulton Park", "red flag on the sighting lap"],
    1: ["highside at Donington in the rain", "long-lap penalty on the last lap"],
    2: ["tank-slapper down Craner Curves", "gravel trap full of your own teeth"],
  },
};

export const PACKS: Pack[] = [office, longHaul, britFilms, superbikes];
